import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getUserNotifications, getUserNotificationsListener, markNotificationAsRead } from '@/api/notifications';
import { NotificationRecord } from '@/types/notifications';

export const useNotifications = () => {
  const { firestoreUser, firebaseUser } = useAuth();
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Load notifications on mount
  useEffect(() => {
    if (!firebaseUser?.uid || !firestoreUser?.id || !firestoreUser?.workspace?.id) {
      setIsLoading(false);
      return;
    }

    const loadNotifications = async () => {
      try {
        setIsLoading(true);
        const response = await getUserNotifications(
          firebaseUser.uid,
          firestoreUser.workspace?.id || '',
          undefined, // filter
          { field: 'timestamp', direction: 'desc' }, // sort
          20 // limit
        );
        
        if (response.success) {
          setNotifications(response.notifications);
          // Calculate unread count
          const unread = response.notifications.filter(notification => 
            !notification.readBy[firestoreUser.id]
          ).length;
          setUnreadCount(unread);
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();
  }, [firebaseUser?.uid, firestoreUser?.id, firestoreUser?.workspace?.id]);

  // Set up real-time listener
  useEffect(() => {
    if (!firebaseUser?.uid || !firestoreUser?.workspace?.id) return;

    const unsubscribe = getUserNotificationsListener(
      firebaseUser.uid,
      firestoreUser.workspace.id,
      (newNotifications) => {
        setNotifications(newNotifications);
        // Calculate unread count
        const unread = newNotifications.filter(notification => 
          !notification.readBy[firestoreUser.id]
        ).length;
        setUnreadCount(unread);
      },
      undefined, // filter
      { field: 'timestamp', direction: 'desc' }, // sort
      20 // limit
    );

    return unsubscribe;
  }, [firebaseUser?.uid, firestoreUser?.workspace?.id]);

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    if (!firestoreUser?.id) return;

    try {
      const response = await markNotificationAsRead({
        notificationId,
        userId: firestoreUser.id
      });

      if (response.success) {
        // Update local state
        setNotifications(prev => prev.map(notification => {
          if (notification.id === notificationId) {
            return {
              ...notification,
              readBy: {
                ...notification.readBy,
                [firestoreUser.id]: new Date() as any
              }
            };
          }
          return notification;
        }));

        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!firestoreUser?.id) return;

    try {
      const unreadNotifications = notifications.filter(notification => 
        !notification.readBy[firestoreUser.id]
      );

      await Promise.all(
        unreadNotifications.map(notification => 
          markNotificationAsRead({
            notificationId: notification.id,
            userId: firestoreUser.id
          })
        )
      );

      // Update local state
      setNotifications(prev => prev.map(notification => ({
        ...notification,
        readBy: {
          ...notification.readBy,
          [firestoreUser.id]: new Date() as any
        }
      })));

      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead
  };
};
